import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { FlashlightLogo } from '../Home/FlashlightLogo';
import { HistoryChart, SPEAKING_STROKE, WRITING_STROKE } from './HistoryChart';
import { QrCode } from './QrCode';
import { HatchPatterns, hatchFor, SkillsChart } from './SkillsChart';
import {
  colorFor,
  formatLevel,
  formatRange,
  HATCH_FILL,
  paragraphsOf,
  speakingCaption,
  speakingLength,
  STRINGS,
  studentSubline,
  teacherLine,
  writingCaption,
  writingLength,
  type AccessLevels,
  type Band,
  type Feedback,
  type FeedbackRun,
  type NextStep,
  type ProgressReport,
  type ReportAccess,
  type ReportBenchmarks,
  type SampleLength,
  type SpeakingSample,
  type WritingSample,
} from './report';
import { REPORT } from './tokens';
import type { FeedbackPart } from './flow';

export const REPORT_TITLE = STRINGS.title;

const small = { fontSize: 12, lineHeight: '16px', fontWeight: 500, color: REPORT.muted } as const;
const h2 = { fontSize: 20, lineHeight: '28px', fontWeight: 800 } as const;
const h3 = { fontSize: 16, lineHeight: '20px', fontWeight: 800, letterSpacing: '-0.5px' } as const;
const label12 = { fontSize: 12, lineHeight: '16px', fontWeight: 700 } as const;

// Text settings shared by a printed sheet and the off-screen pass that
// measures its sections, so both lay text out the same way.
export const sheetText = {
  color: REPORT.ink,
  fontFamily: REPORT.font,
  overflowWrap: 'anywhere',
  WebkitFontSmoothing: 'antialiased',
  '& *': { boxSizing: 'border-box' },
} as const;

export function PageFrame({
  report,
  page,
  pageCount,
  children,
}: {
  report: ProgressReport;
  page: number;
  pageCount: number;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      className="report-page"
      data-testid="report-page"
      aria-label={STRINGS.page(page, pageCount)}
      sx={{
        width: REPORT.sheet.width,
        height: REPORT.sheet.height,
        flexShrink: 0,
        boxSizing: 'border-box',
        padding: `${REPORT.sheet.padding}px`,
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        boxShadow: REPORT.cardShadow,
        ...sheetText,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: REPORT.sheet.inner }}>
        {page === 1 && <HatchPatterns />}
        <PageHeader report={report} page={page} pageCount={pageCount} />
        {children}
      </Box>
    </Box>
  );
}

export function PageHeader({ report, page, pageCount }: { report: ProgressReport; page: number; pageCount: number }) {
  return (
    <Box component="header" sx={{ pb: '12px', borderBottom: `2px solid ${REPORT.ink}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <FlashlightLogo height={28} variant="dark" />
          <Typography sx={{ ...small, fontWeight: 600, letterSpacing: '0.4px' }}>{report.student.district}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.3px' }}>{REPORT_TITLE}</Typography>
          <Typography sx={small}>
            {report.schoolYear} · {STRINGS.page(page, pageCount)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function StudentBanner({ report }: { report: ProgressReport }) {
  const teacher = teacherLine(report.student.teacher);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '24px',
        py: '8px',
        borderBottom: `1px solid ${REPORT.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
        <Typography data-testid="sheet-name" sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 800, letterSpacing: '-0.5px' }}>
          {report.student.fullName || STRINGS.student}
        </Typography>
        <Typography sx={small}>{studentSubline(report.student)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', maxWidth: '50%', textAlign: 'right' }}>
        {teacher && <Typography sx={label12}>{teacher}</Typography>}
        <Typography sx={small}>{STRINGS.prepared(report.preparedOn)}</Typography>
      </Box>
    </Box>
  );
}

function Swatch({ fill, testId }: { fill: string; testId?: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }} data-testid={testId}>
      <rect width={16} height={16} rx={4} fill={fill} />
    </svg>
  );
}

function LineKey() {
  const item = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: 12, lineHeight: '16px', fontWeight: 800 } as const;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
      <Box sx={item}>
        <svg width={32} height={12} viewBox="0 0 32 12" aria-hidden="true">
          <line x1={0} y1={6} x2={32} y2={6} stroke={SPEAKING_STROKE} strokeWidth={2.5} />
          <circle cx={16} cy={6} r={4.5} fill={SPEAKING_STROKE} />
        </svg>
        {STRINGS.speaking}
      </Box>
      <Box sx={{ ...item, color: REPORT.muted }}>
        <svg width={32} height={12} viewBox="0 0 32 12" aria-hidden="true">
          <line x1={0} y1={6} x2={32} y2={6} stroke={WRITING_STROKE} strokeWidth={2.5} strokeDasharray="7 5" />
          <rect x={12} y={2} width={8} height={8} fill={WRITING_STROKE} />
        </svg>
        {STRINGS.writing}
      </Box>
    </Box>
  );
}

export function NothingYet({ report }: { report: ProgressReport }) {
  return (
    <Typography data-testid="nothing-yet" sx={{ ...small, py: '16px' }}>
      {STRINGS.nothingYet(report.student.firstName || STRINGS.theStudent)}
    </Typography>
  );
}

export function HistorySection({ report }: { report: ProgressReport }) {
  const { benchmarks } = report;
  if (benchmarks.history.length < 2) return null;
  return (
    <Box data-testid="growth-section" sx={{ pt: '16px', pb: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Typography component="h2" sx={h2}>
        {STRINGS.growthTitle}
      </Typography>
      <Typography component="h3" data-testid="growth-subtitle" sx={h3}>
        {STRINGS.growthSubtitle(benchmarks.history.length)}
      </Typography>
      <LineKey />
      <HistoryChart history={benchmarks.history} top={benchmarks.top} />
    </Box>
  );
}

function YearLegend({ benchmarks }: { benchmarks: ReportBenchmarks }) {
  const shown = benchmarks.shown.length;
  return (
    <Box data-testid="year-legend" sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
      {benchmarks.shown.map((window, j) => {
        const current = j === shown - 1;
        return (
          <Box
            key={window.date}
            sx={{ display: 'flex', alignItems: 'center', gap: '4px', ...label12, color: current ? REPORT.ink : REPORT.muted, whiteSpace: 'nowrap' }}
          >
            <Swatch fill={current ? REPORT.faint : hatchFor(j, shown)} testId={current ? 'legend-current' : 'legend-earlier'} />
            {window.legend}
          </Box>
        );
      })}
    </Box>
  );
}

function OverallRow({ benchmarks }: { benchmarks: ReportBenchmarks }) {
  const shown = benchmarks.shown.length;
  return (
    <Box
      data-testid="overall-row"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px',
        padding: '8px 16px',
        borderRadius: '8px',
        outline: `1px solid ${REPORT.grid}`,
        whiteSpace: 'nowrap',
      }}
    >
      {benchmarks.domains.map((domain) => (
        <Box key={domain.domain} sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 800, letterSpacing: '1px', flexShrink: 0 }}>
            {STRINGS.domainOverall(domain.domain)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {domain.overall.map((value, j) => {
              const current = j === shown - 1;
              return (
                <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {j > 0 && value !== null && domain.overall[j - 1] !== null && (
                    <Box component="span" sx={{ ...label12, color: REPORT.muted }}>
                      →
                    </Box>
                  )}
                  <Box component="span" data-testid="overall-chip" sx={{ display: 'flex', alignItems: 'center', gap: '4px', pr: '4px', ...label12 }}>
                    <Swatch fill={current ? colorFor(benchmarks.bands, value) : hatchFor(j, shown)} />
                    {formatLevel(value)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function LevelsLegend({ bands }: { bands: Band[] }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: '8px', pb: '12px' }}>
      <Box data-testid="levels-legend" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: '16px', rowGap: '4px', alignItems: 'flex-start', whiteSpace: 'nowrap' }}>
        <Box sx={{ ...label12, flexShrink: 0 }}>{STRINGS.proficiencyLevels}</Box>
        {bands.map((band) => {
          const color = band.color || HATCH_FILL;
          return (
            <Box key={band.label} sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexShrink: 0 }}>
              <Box
                component="span"
                sx={{ display: 'flex', gap: '2px', borderRadius: '4px', overflow: 'clip', pr: '4px', outline: `1px solid ${color}` }}
              >
                <svg width={8} height={16} viewBox="0 0 8 16" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
                  <rect width={8} height={16} fill={color} />
                </svg>
                <Box component="b" sx={{ ...small, color: REPORT.ink }}>
                  {formatRange(band)}
                </Box>
              </Box>
              <Box component="span" sx={small}>
                {band.label}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function SkillsSection({ report }: { report: ProgressReport }) {
  const { benchmarks } = report;
  if (benchmarks.shown.length === 0) return null;
  return (
    <Box data-testid="skills-section" sx={{ pt: '12px', pb: '8px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', pb: '12px' }}>
        <Typography component="h3" data-testid="skills-title" sx={h3}>
          {STRINGS.skillsTitle(benchmarks.shown.length)}
        </Typography>
        <YearLegend benchmarks={benchmarks} />
      </Box>
      <OverallRow benchmarks={benchmarks} />
      <SkillsChart domains={benchmarks.domains} bands={benchmarks.bands} top={benchmarks.top} shown={benchmarks.shown.length} />
      <LevelsLegend bands={benchmarks.bands} />
    </Box>
  );
}

export function Finding({ report }: { report: ProgressReport }) {
  if (report.benchmarks.narrative.length === 0) return null;
  return (
    <Typography data-testid="narrative" sx={{ pb: '20px', fontSize: 16, lineHeight: '20px', fontWeight: 600, letterSpacing: '-0.5px' }}>
      {report.benchmarks.narrative.join(' ')}
    </Typography>
  );
}

function accessTestName(access: ReportAccess): string {
  return access.scale === 'alternate_access' ? 'Alternate ACCESS' : 'ACCESS';
}

function accessScaleMax(access: ReportAccess): number {
  return access.scale === 'alternate_access' ? 5 : 6;
}

const ACCESS_CELLS: Array<{ key: keyof Omit<AccessLevels, 'label'>; width?: number }> = [
  { key: 'listening', width: 100 },
  { key: 'speaking', width: 100 },
  { key: 'reading', width: 98 },
  { key: 'writing', width: 94 },
  { key: 'overall' },
];

export function AccessSection({ report }: { report: ProgressReport }) {
  const access = report.access;
  if (!access) return null;
  const test = accessTestName(access);
  const prior = access.prior;
  const value = { fontSize: 20, lineHeight: '24px', fontWeight: 800 } as const;
  return (
    <Box
      data-testid="access-section"
      sx={{ borderTop: `1px solid ${REPORT.divider}`, pt: '16px', pb: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '16px', whiteSpace: 'nowrap' }}>
        <Typography component="h2" sx={{ ...h2, flexShrink: 0 }}>
          {STRINGS.accessTitle(test)}
        </Typography>
        <Typography data-testid="access-label" sx={small}>
          {prior ? `${prior.label} → ${access.label}` : access.label}
        </Typography>
        <Typography sx={small}>{STRINGS.accessScale(test, accessScaleMax(access))}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: '16px' }}>
        {ACCESS_CELLS.map((cell) => {
          const overall = cell.key === 'overall';
          return (
            <Box
              key={cell.key}
              data-testid={`access-${cell.key}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                flexShrink: 0,
                ...(overall ? { flex: '1 1 0', pl: '14px', borderLeft: `1px solid ${REPORT.divider}` } : { width: cell.width }),
              }}
            >
              <Typography sx={{ ...small, fontWeight: overall ? 800 : 600, color: overall ? REPORT.ink : REPORT.muted }}>
                {STRINGS.accessDomains[cell.key]}
              </Typography>
              <Box sx={{ display: 'flex', gap: '4px', ...value }}>
                {prior && prior[cell.key] != null && access[cell.key] != null && (
                  <>
                    <Box component="span" sx={{ color: REPORT.muted }}>
                      {formatLevel(prior[cell.key])}
                    </Box>
                    <Box component="span" sx={{ color: REPORT.muted }}>
                      →
                    </Box>
                  </>
                )}
                <span>{formatLevel(access[cell.key])}</span>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const sampleTitle = { fontSize: 24, lineHeight: '32px', fontWeight: 800, letterSpacing: '-0.5px' } as const;

function SamplePage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pt: '16px', pb: '32px' }}>
      <Typography component="h2" sx={sampleTitle}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '32px' }}>{children}</Box>
    </Box>
  );
}

function LengthBlock({ length }: { length: SampleLength | null }) {
  if (!length) return null;
  return (
    <Box data-testid="sample-length" sx={{ padding: '12px 20px', borderRadius: '8px', backgroundColor: REPORT.screenBg }}>
      <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, color: REPORT.ink }}>
        <Box component="span" sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 800, letterSpacing: '-1px', pr: '4px' }}>
          {length.value}
        </Box>{' '}
        {length.unit}
      </Typography>
    </Box>
  );
}

function SampleColumn({
  caption,
  current,
  length,
  prompt,
  children,
}: {
  caption: string;
  current: boolean;
  length: SampleLength | null;
  prompt: string;
  children: ReactNode;
}) {
  return (
    <Box data-testid="sample-column" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: 344, flexShrink: 0 }}>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 800,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: current ? REPORT.navy : REPORT.muted,
        }}
      >
        {caption}
      </Typography>
      <LengthBlock length={length} />
      {prompt && <Typography sx={{ fontSize: 16, lineHeight: '20px', fontWeight: 500, color: REPORT.muted }}>{STRINGS.task(prompt)}</Typography>}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '16px',
          border: `1px solid ${REPORT.divider}`,
          borderRadius: '8px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Quote({ text, shortened }: { text: string; shortened: boolean }) {
  const paragraphs = paragraphsOf(text);
  const last = paragraphs.length - 1;
  return (
    <>
      {paragraphs.map((paragraph, i) => (
        <Typography key={i} component="p" sx={{ fontSize: 16, lineHeight: '20px', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
          {i === 0 ? '“' : ''}
          {paragraph}
          {i === last ? '”' : ''}
        </Typography>
      ))}
      {shortened && (
        <Typography data-testid="shortened-note" sx={small}>
          {STRINGS.shortened}
        </Typography>
      )}
    </>
  );
}

function WritingColumn({ sample, current }: { sample: WritingSample; current: boolean }) {
  return (
    <SampleColumn current={current} caption={writingCaption(sample)} length={writingLength(sample)} prompt={sample.prompt}>
      <Quote text={sample.text} shortened={sample.shortened} />
    </SampleColumn>
  );
}

export function WritingPage({ report }: { report: ProgressReport }) {
  const samples: WritingSample[] = report.samples.writing;
  return (
    <SamplePage title={STRINGS.writingTitle(report.student.firstName || STRINGS.theStudent)}>
      {samples.map((sample, i) => (
        <WritingColumn key={sample.label} sample={sample} current={i === samples.length - 1} />
      ))}
    </SamplePage>
  );
}

function ListenBlock({ sample, first }: { sample: SpeakingSample; first: string }) {
  if (!sample.listenUrl) return null;
  return (
    <Box
      data-testid="listen-block"
      sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: '16px', pt: '16px', borderTop: `1px solid ${REPORT.divider}`, breakInside: 'avoid' }}
    >
      <QrCode text={sample.listenUrl} size={80} label={STRINGS.listenAria(first, sample.label)} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 800 }}>{STRINGS.listenTitle}</Typography>
        <Typography sx={{ fontSize: 16, lineHeight: '20px', fontWeight: 500, color: REPORT.muted }}>{STRINGS.listenHint(first)}</Typography>
      </Box>
    </Box>
  );
}

function SpeakingColumn({ sample, current, first }: { sample: SpeakingSample; current: boolean; first: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: 344, flexShrink: 0 }}>
      <SampleColumn current={current} caption={speakingCaption(sample)} length={speakingLength(sample)} prompt={sample.prompt}>
        {sample.transcript != null ? (
          <Quote text={sample.transcript} shortened={sample.shortened} />
        ) : (
          <Typography component="p" sx={{ fontSize: 16, lineHeight: '20px', fontWeight: 500, color: REPORT.muted }}>
            {STRINGS.noTranscription}
          </Typography>
        )}
      </SampleColumn>
      <ListenBlock sample={sample} first={first} />
    </Box>
  );
}

export function SpeakingPage({ report }: { report: ProgressReport }) {
  const samples: SpeakingSample[] = report.samples.speaking;
  const first = report.student.firstName || STRINGS.theStudent;
  return (
    <SamplePage title={STRINGS.speakingTitle(first)}>
      {samples.map((sample, i) => (
        <SpeakingColumn key={sample.label} sample={sample} current={i === samples.length - 1} first={first} />
      ))}
    </SamplePage>
  );
}

// One writing and one speaking sample, side by side. The titles share a grid
// row so both samples start level even when a long name wraps one title.
export function SamplesPair({ report }: { report: ProgressReport }) {
  const first = report.student.firstName || STRINGS.theStudent;
  const writing = report.samples.writing[0]!;
  const speaking = report.samples.speaking[0]!;
  return (
    <Box
      data-testid="samples-pair"
      sx={{ display: 'grid', gridTemplateColumns: '344px 344px', columnGap: '32px', rowGap: '12px', alignItems: 'start', pt: '16px', pb: '32px' }}
    >
      <Typography component="h2" sx={sampleTitle}>
        {STRINGS.writingTitle(first)}
      </Typography>
      <Typography component="h2" sx={sampleTitle}>
        {STRINGS.speakingTitle(first)}
      </Typography>
      <WritingColumn sample={writing} current />
      <SpeakingColumn sample={speaking} current first={first} />
    </Box>
  );
}

const caption = { fontSize: 12, lineHeight: '16px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase' } as const;

function EndingSection({
  testId,
  title,
  label,
  header = true,
  top = '16px',
  bottom = '32px',
  children,
}: {
  testId: string;
  title: string;
  label: string;
  header?: boolean;
  top?: string;
  bottom?: string;
  children: ReactNode;
}) {
  return (
    <Box data-testid={testId} sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pt: top, pb: bottom }}>
      {header && (
        <>
          <Typography component="h2" sx={sampleTitle}>
            {title}
          </Typography>
          <Typography sx={{ ...caption, color: REPORT.navy }}>{STRINGS.benchmark(label)}</Typography>
        </>
      )}
      {children}
    </Box>
  );
}

function NextStepBox({ domain, step }: { domain: string; step: NextStep }) {
  return (
    <Box
      data-testid="next-step-box"
      sx={{ width: 344, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px', border: `1px solid ${REPORT.divider}`, borderRadius: '8px' }}
    >
      <Typography sx={{ ...caption, color: REPORT.muted }}>{domain}</Typography>
      <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.5px' }}>{step.heading}</Typography>
      <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500 }}>{step.definition}</Typography>
    </Box>
  );
}

export function NextStepsSection({ report }: { report: ProgressReport }) {
  const steps = report.nextSteps!;
  return (
    <EndingSection testId="next-steps-section" title={STRINGS.nextStepsTitle(report.student.firstName || STRINGS.theStudent)} label={steps.label}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '32px', width: 720 }}>
        {steps.speaking && <NextStepBox domain={STRINGS.speaking} step={steps.speaking} />}
        {steps.writing && <NextStepBox domain={STRINGS.writing} step={steps.writing} />}
      </Box>
    </EndingSection>
  );
}

function FeedbackRunText({ run }: { run: FeedbackRun }) {
  return (
    <Box
      component="span"
      sx={{ fontWeight: run.bold ? 800 : undefined, fontStyle: run.italic ? 'italic' : undefined, textDecoration: run.underline ? 'underline' : undefined }}
    >
      {run.text}
    </Box>
  );
}

export function FeedbackSection({ report, part }: { report: ProgressReport; part?: FeedbackPart }) {
  const feedback: Feedback = report.feedback!;
  const paragraphs = part ? [part.runs] : feedback.paragraphs;
  const opensSection = part ? part.opensSection : true;
  return (
    <EndingSection
      testId="feedback-section"
      title={STRINGS.feedbackTitle(report.student.firstName || STRINGS.theStudent)}
      label={feedback.label}
      header={opensSection}
      top={opensSection ? '16px' : part?.opensParagraph ? '12px' : '0px'}
      bottom={!part || part.closesSection ? '32px' : '0px'}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {paragraphs.map((runs, i) => (
          <Typography key={i} component="p" data-testid="feedback-paragraph" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500 }}>
            {runs.map((run, j) => (
              <FeedbackRunText key={j} run={run} />
            ))}
          </Typography>
        ))}
      </Box>
    </EndingSection>
  );
}
